import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TelegramIcon from '@mui/icons-material/Telegram';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { api } from '../documents/docApi';
import { orchard } from '../../theme';

const IDEAS = [
  '«Что у меня завтра?», «итоги недели по группе»',
  '«Аскар сегодня молодец, выучил цвета» — Черри запомнит',
  'Голосовые — ответ придёт текстом и голосом',
  '«Сделай домашку / лист / песенку» — придёт файлом',
  '«Отправь отчёт родителям группы» — кнопка «Отправить»',
];

/* Link the teacher's own Telegram to the school bot: open the bot from the link,
   or send it the 6-digit code. Then Черри answers in Telegram too. */
export default function TelegramDialog({ open, onClose, onChange }) {
  const [state, setState] = useState(null); // { linked, bot, botUrl }
  const [code, setCode] = useState(null); // { code, url, bot, expiresInMin }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await api('/api/telegram/me');
      setState(s);
      if (onChange) onChange(s.linked);
      return s;
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, [onChange]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setCode(null);
    load();
  }, [open, load]);

  // waiting for the bot to see the code
  useEffect(() => {
    if (!open || !code || (state && state.linked)) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [open, code, state, load]);

  const link = async () => {
    setBusy(true);
    setError('');
    try {
      setCode(await api('/api/telegram/link', { method: 'POST' }));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const unlink = async () => {
    if (
      !window.confirm(
        'Отвязать Telegram? Черри и напоминания перестанут туда приходить.'
      )
    )
      return;
    setBusy(true);
    try {
      await api('/api/telegram/unlink', { method: 'POST' });
      setCode(null);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const copy = () => {
    try {
      navigator.clipboard.writeText(code.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // clipboard blocked — the code is on screen
    }
  };

  const linked = state && state.linked;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TelegramIcon sx={{ color: '#229ED9' }} /> Telegram
      </DialogTitle>
      <DialogContent sx={{ display: 'grid', gap: 1.5 }}>
        {!state && !error && <CircularProgress size={24} />}
        {linked && (
          <Alert icon={<CheckCircleRoundedIcon />} severity="success">
            Telegram привязан. Пишите боту
            {state.bot ? ` @${state.bot}` : ''} — Черри ответит там, туда же
            придут напоминания и документы.
          </Alert>
        )}
        {state && !linked && !code && (
          <Typography variant="body2">
            Привяжите свой Telegram — и с Черри можно говорить прямо там:
            текстом и голосом, файлы и отчёты придут туда же.
          </Typography>
        )}
        {state && !linked && code && (
          <Box sx={{ display: 'grid', gap: 1.25 }}>
            <Button
              variant="contained"
              size="large"
              href={code.url}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<TelegramIcon />}
              sx={{ bgcolor: '#229ED9', '&:hover': { bgcolor: '#1b8bc0' } }}
            >
              Открыть @{code.bot} и нажать «Старт»
            </Button>
            <Typography variant="body2" color="text.secondary">
              Или напишите боту @{code.bot} этот код (действует{' '}
              {code.expiresInMin} минут):
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                py: 1,
                borderRadius: '14px',
                bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
              }}
            >
              <Typography
                sx={{
                  fontFamily: orchard.fontMono,
                  fontSize: '2rem',
                  fontWeight: 800,
                  letterSpacing: '0.12em',
                }}
              >
                {code.code.slice(0, 3)} {code.code.slice(3)}
              </Typography>
              <Tooltip title={copied ? 'Скопировано' : 'Скопировать'}>
                <IconButton onClick={copy}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <CircularProgress size={14} />
              <Typography variant="caption">
                Жду, когда бот увидит код…
              </Typography>
            </Box>
          </Box>
        )}
        {state && (
          <Box component="ul" sx={{ m: 0, pl: 2.5, color: 'text.secondary' }}>
            {IDEAS.map((x) => (
              <Typography component="li" variant="body2" key={x}>
                {x}
              </Typography>
            ))}
          </Box>
        )}
        {error && <Alert severity="error">{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {linked && state.botUrl && (
          <Button
            href={state.botUrl}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<TelegramIcon />}
          >
            Открыть бота
          </Button>
        )}
        {linked && (
          <Button color="error" onClick={unlink} disabled={busy}>
            Отвязать
          </Button>
        )}
        {state && !linked && !code && (
          <Button variant="contained" onClick={link} disabled={busy}>
            Привязать
          </Button>
        )}
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
